import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay, faPause, faVolumeHigh, faVolumeXmark,
  faExpand, faCompress,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import PlayButtonK from "./PlayButtonK";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

// ponytail: player chrome stays dark in light theme on purpose - it sits on top
// of the video frame, not on the page background, so it needs no light override.
export default function VideoPlayer({
  src,
  poster,
  autoPlay = false,
  onEnded,
  className = "",
  videoClassName = "tw-w-full tw-h-full tw-object-contain",
}) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hideTimer = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(autoPlay);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      setHasStarted(true);
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, []);

  const revealControls = useCallback(() => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 3000);
  }, []);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  useEffect(() => {
    const onFsChange = () =>
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {});
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleSeek = (e) => {
    const video = videoRef.current;
    const next = Number(e.target.value);
    if (video && Number.isFinite(next)) {
      video.currentTime = next;
      setCurrentTime(next);
    }
  };

  const handleVolume = (e) => {
    const video = videoRef.current;
    const next = Number(e.target.value);
    if (!video) return;
    video.volume = next;
    video.muted = next === 0;
    setVolume(next);
    setIsMuted(next === 0);
  };

  const handleProgress = () => {
    const video = videoRef.current;
    if (video && video.buffered.length > 0) {
      setBuffered(video.buffered.end(video.buffered.length - 1));
    }
  };

  // Space and arrows on the container, so the whole player is keyboard drivable.
  const handleKeyDown = (e) => {
    const video = videoRef.current;
    if (!video) return;
    if (e.key === " " || e.key === "k") {
      e.preventDefault();
      togglePlay();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      video.currentTime = Math.min(video.currentTime + 5, video.duration || 0);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      video.currentTime = Math.max(video.currentTime - 5, 0);
    }
    revealControls();
  };

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration ? (buffered / duration) * 100 : 0;
  const volumePercent = (isMuted ? 0 : volume) * 100;

  return (
    <div
      ref={containerRef}
      className={`tw-relative tw-bg-black tw-overflow-hidden ${className}`}
      onMouseMove={revealControls}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-label={t("Video player")}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay={autoPlay}
        playsInline
        preload="metadata"
        className={videoClassName}
        onClick={togglePlay}
        onPlay={() => { setIsPlaying(true); setHasStarted(true); revealControls(); }}
        onPause={() => { setIsPlaying(false); setShowControls(true); }}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onProgress={handleProgress}
        onEnded={(e) => { setIsPlaying(false); setShowControls(true); onEnded?.(e); }}
      />

      {/* Poster state: the branded K sits centre until the first play. */}
      {!hasStarted && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-black/25">
          <PlayButtonK size="lg" onClick={togglePlay} />
        </div>
      )}

      {/* Scrim: controls sit on arbitrary film frames and vanish on bright footage without it. */}
      <div
        className={`tw-absolute tw-inset-x-0 tw-bottom-0 tw-px-3 md:tw-px-4 tw-pt-10 tw-pb-2 md:tw-pb-3
          tw-bg-gradient-to-t tw-from-black/90 tw-via-black/50 tw-to-transparent
          tw-transition-opacity tw-duration-300
          ${showControls || !isPlaying ? "tw-opacity-100" : "tw-opacity-0 tw-pointer-events-none"}`}
      >
        <input
          type="range"
          min={0}
          max={duration || 0}
          step="any"
          value={currentTime}
          onChange={handleSeek}
          aria-label={t("Seek")}
          className="kk-video-range tw-w-full tw-mb-1 md:tw-mb-2"
          style={{
            background: `linear-gradient(to right,
              #ff43a7 0%, #ff43a7 ${progressPercent}%,
              rgba(255,176,207,0.45) ${progressPercent}%, rgba(255,176,207,0.45) ${bufferedPercent}%,
              rgba(255,255,255,0.25) ${bufferedPercent}%, rgba(255,255,255,0.25) 100%)`,
          }}
        />

        <div className="tw-flex tw-items-center tw-gap-3 md:tw-gap-4 tw-text-white">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? t("Pause") : t("Play")}
            className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-lg tw-text-white hover:tw-text-[#ff43a7] focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#ff43a7] tw-rounded tw-transition-colors"
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>

          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={isMuted ? t("Unmute") : t("Mute")}
              className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-white hover:tw-text-[#ff43a7] focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#ff43a7] tw-rounded tw-transition-colors"
            >
              <FontAwesomeIcon icon={isMuted ? faVolumeXmark : faVolumeHigh} />
            </button>
            {/* Hidden on small screens: phones have hardware volume keys. */}
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={isMuted ? 0 : volume}
              onChange={handleVolume}
              aria-label={t("Volume")}
              className="kk-video-range tw-hidden md:tw-block tw-w-20"
              style={{
                background: `linear-gradient(to right, #ff43a7 0%, #ff43a7 ${volumePercent}%, rgba(255,255,255,0.25) ${volumePercent}%, rgba(255,255,255,0.25) 100%)`,
              }}
            />
          </div>

          <span className="tw-text-xs md:tw-text-sm tw-tabular-nums tw-select-none">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? t("Exit fullscreen") : t("Fullscreen")}
            className="tw-ml-auto tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-text-white hover:tw-text-[#ff43a7] focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#ff43a7] tw-rounded tw-transition-colors"
          >
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </button>
        </div>
      </div>
    </div>
  );
}
